import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/apiService';
import LegalLinks from './LegalLinks';
import { useLanguage } from '../i18n';

// Signup email verification — the 6-digit code gate (#335).
//
// Rendered INSTEAD of the workspace while the backend reports the account still
// owes a verification, which makes the gate structural rather than advisory:
// App.jsx never mounts a protected route for an unverified account, and the
// backend refuses every non-allowlisted request for one anyway. Neither half
// relies on the other.
//
// Three behaviours are deliberate:
//
//  • **The code is requested automatically on arrival.** Landing on a code box
//    with no code in your inbox and a "Send it" button you have to find first is
//    a dead end for the least attentive user at the least forgiving moment. The
//    server's own cooldown makes an accidental double-request harmless — it
//    answers 429 with a retry_after, which is rendered as the countdown rather
//    than as an error.
//  • **Errors are typed, not generic.** "Invalid code" is useless when the real
//    situation is "that one expired" or "you have one attempt left". The backend
//    gives each failure its own `type`; every one of them gets its own sentence.
//  • **Sign out is always offered.** A user who cannot receive the email must
//    not be trapped on this screen — this is the FE half of the same rule the
//    backend gate encodes by allowlisting logout and account deletion.
const CODE_LENGTH = 6;

const VerifyEmailPage = ({ email, onVerified, onLogout }) => {
    const { t } = useLanguage();
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [busy, setBusy] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const requestedRef = useRef(false);

    const applyRequestOutcome = useCallback((err) => {
        const type = err?.body?.error?.type;
        if (type === 'cooldown') {
            // Not an error: a code is already in flight. Show the countdown.
            setCooldown(Number(err?.body?.retry_after) || 60);
            setStatus(t('verifyCodeAlreadySent', 'A code is already on its way — check your inbox.'));
            return;
        }
        if (type === 'send_failed') {
            setError(t('verifySendFailed', "We couldn't send the email just now. Please try again in a moment."));
            return;
        }
        if (type === 'not_required') {
            // Nothing outstanding (verified elsewhere, or the gate was turned
            // off while this screen was open) — re-read and let the gate lift.
            onVerified?.();
            return;
        }
        setError(t('verifySendFailed', "We couldn't send the email just now. Please try again in a moment."));
    }, [onVerified, t]);

    const requestCode = useCallback(async ({ manual = false } = {}) => {
        setError('');
        if (manual) setStatus('');
        setBusy(true);
        try {
            await apiService.post('/auth/email-verification/request', {});
            setCooldown(60);
            setStatus(t('verifyCodeSent', 'We sent a 6-digit code to your email.'));
        } catch (err) {
            applyRequestOutcome(err);
        } finally {
            setBusy(false);
        }
    }, [applyRequestOutcome, t]);

    // One automatic request per mount. The ref (not state) is what makes it
    // exactly once even under React 18 StrictMode's dev double-mount.
    useEffect(() => {
        if (requestedRef.current) return;
        requestedRef.current = true;
        requestCode();
    }, [requestCode]);

    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setStatus('');
        if (code.length !== CODE_LENGTH) {
            setError(t('verifyCodeIncomplete', 'Enter the 6-digit code from your email.'));
            return;
        }
        setBusy(true);
        try {
            await apiService.post('/auth/email-verification/verify', { code });
            setStatus(t('verifyDone', 'Email confirmed — opening PAI…'));
            await onVerified?.();
        } catch (err) {
            const type = err?.body?.error?.type;
            const left = err?.body?.attempts_left;
            if (type === 'expired') {
                setError(t('verifyExpired', 'That code has expired — request a new one.'));
            } else if (type === 'too_many_attempts') {
                setError(t('verifyTooManyAttempts', 'Too many attempts. Request a new code.'));
            } else if (type === 'no_code') {
                setError(t('verifyNoCode', 'No code has been sent yet — request one first.'));
            } else if (typeof left === 'number') {
                setError(left > 0
                    ? t('verifyInvalidWithAttempts', 'That code is not correct.') + ` (${left})`
                    : t('verifyTooManyAttempts', 'Too many attempts. Request a new code.'));
            } else {
                setError(t('verifyInvalid', 'That code is not correct.'));
            }
            setCode('');
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="verify-email-screen"
            className="relative flex h-dvh overflow-y-auto bg-pai-void text-white"
        >
            <div
                aria-hidden="true"
                className="fixed inset-0 bg-cover bg-center"
                style={{ backgroundImage: "url('/wallpaper.jpg')" }}
            />
            <div aria-hidden="true" className="fixed inset-0 bg-[rgba(5,7,10,0.64)]" />
            {/* #432: `m-auto` centres the card while it fits and lets the page
                scroll when it does not (a phone held sideways, a short window).
                `items-center` on the parent used to centre it OUTSIDE the
                viewport instead, cutting off the top and the submit button. */}
            <div className="relative w-full max-w-md px-6 py-6 m-auto">
                <div className="w-full p-8 space-y-6 rounded-modal border border-white/[0.14] bg-white/[0.03] backdrop-blur-tile">
                    <div className="text-center">
                        <h2 className="text-[28px] font-extrabold tracking-[-0.025em] text-white">
                            {t('verifyTitle', 'Confirm your email')}
                        </h2>
                        <p className="mt-2 text-sm text-pai-meta">
                            {t('verifySubtitle', 'Enter the 6-digit code we sent to')}
                        </p>
                        <p data-testid="verify-email-address"
                           className="mt-1 text-sm font-semibold text-white break-all">
                            {email}
                        </p>
                    </div>

                    {error && (
                        <div
                            data-testid="verify-error"
                            role="alert"
                            className="p-4 text-sm text-pai-red border border-[rgba(240,68,56,0.4)] bg-[rgba(240,68,56,0.08)] rounded-ctl"
                        >
                            {error}
                        </div>
                    )}
                    {!error && status && (
                        <div
                            data-testid="verify-status"
                            role="status"
                            className="p-4 text-sm text-center text-white border border-white/[0.14] bg-white/[0.05] rounded-ctl"
                        >
                            {status}
                        </div>
                    )}

                    <form className="space-y-6" onSubmit={submit} noValidate>
                        <input
                            id="verification-code"
                            name="verification-code"
                            data-testid="verify-code-input"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            autoFocus
                            maxLength={CODE_LENGTH}
                            aria-label={t('verifyTitle', 'Confirm your email')}
                            value={code}
                            onChange={(e) => setCode(
                                e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                            className="w-full h-14 px-4 text-center text-2xl font-bold tracking-[0.5em] text-white bg-white/[0.05] border border-white/[0.14] rounded-ctl focus:outline-none focus:border-white/40"
                            placeholder="------"
                        />
                        <button
                            type="submit"
                            data-testid="verify-submit"
                            disabled={busy || code.length !== CODE_LENGTH}
                            className="w-full h-11 px-4 text-sm font-semibold text-pai-void bg-white rounded-ctl hover:bg-white/90 transition-colors focus:outline-none disabled:opacity-50"
                        >
                            {t('verifyConfirm', 'Confirm email')}
                        </button>
                    </form>

                    <div className="flex items-center justify-between text-sm">
                        <button
                            type="button"
                            data-testid="verify-resend"
                            disabled={busy || cooldown > 0}
                            onClick={() => requestCode({ manual: true })}
                            className="text-pai-meta hover:text-white transition-colors disabled:opacity-50"
                        >
                            {cooldown > 0
                                ? `${t('verifyResend', 'Resend code')} (${cooldown})`
                                : t('verifyResend', 'Resend code')}
                        </button>
                        {/* Always offered: an account you can neither use nor
                            leave is a trap. */}
                        <button
                            type="button"
                            data-testid="verify-logout"
                            onClick={onLogout}
                            className="text-pai-meta hover:text-white transition-colors"
                        >
                            {t('logout', 'Logout')}
                        </button>
                    </div>
                </div>
                <LegalLinks className="mt-6" />
            </div>
        </div>
    );
};

export default VerifyEmailPage;
